import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Organization, OrganizationGroup } from '../../services/users/users.service';

export interface ContextPickerResult {
  groupId?: number;
  organizationId: number;
}

@Component({
  selector: 'app-firm-context-picker',
  templateUrl: './firm-context-picker.component.html',
  styleUrls: ['./firm-context-picker.component.scss'],
})
export class FirmContextPickerComponent implements OnChanges {
  /** 'super-admin' shows group + org dropdowns; 'supervisor' shows org dropdown only. */
  @Input() mode: 'super-admin' | 'supervisor' = 'supervisor';
  /** Groups for super-admin mode. */
  @Input() groups: OrganizationGroup[] = [];
  /** Orgs to show in the org dropdown. Updated by parent when group changes (super-admin mode). */
  @Input() orgs: Organization[] = [];

  /** Emitted when super admin changes group so parent can load new orgs. */
  @Output() groupChange = new EventEmitter<number>();
  /** Emitted on "Continue" click. Parent must call switch endpoints then dismiss. */
  @Output() contextSelected = new EventEmitter<ContextPickerResult>();

  selectedGroupId: number | null = null;
  selectedOrgId: number | null = null;

  get sortedGroups(): OrganizationGroup[] {
    return [...this.groups].sort((a, b) => a.GROUP_NAME.localeCompare(b.GROUP_NAME));
  }

  get sortedOrgs(): Organization[] {
    return [...this.orgs].sort((a, b) => a.ORGANIZATION_NAME.localeCompare(b.ORGANIZATION_NAME));
  }

  get canConfirm(): boolean {
    if (this.mode === 'super-admin') {
      return this.selectedGroupId !== null && this.selectedOrgId !== null;
    }
    return this.selectedOrgId !== null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['orgs']) {
      // Reset org selection when the org list changes (e.g. after group change).
      this.selectedOrgId = null;
    }
  }

  onGroupChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const raw = select.value;
    if (raw === '') {
      this.selectedGroupId = null;
      return;
    }
    const groupId = Number(raw);
    if (Number.isNaN(groupId)) return;
    this.selectedGroupId = groupId;
    this.selectedOrgId = null;
    this.groupChange.emit(groupId);
  }

  onOrgChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const raw = select.value;
    if (raw === '') {
      this.selectedOrgId = null;
      return;
    }
    const orgId = Number(raw);
    if (Number.isNaN(orgId)) return;
    this.selectedOrgId = orgId;
  }

  onConfirm(): void {
    if (!this.canConfirm || this.selectedOrgId === null) return;
    const result: ContextPickerResult = { organizationId: this.selectedOrgId };
    if (this.mode === 'super-admin' && this.selectedGroupId !== null) {
      result.groupId = this.selectedGroupId;
    }
    this.contextSelected.emit(result);
  }
}
